import type { ComponentProps, ReactNode } from 'react'
import { useId } from 'react'

import { cn } from '@/shared/lib'
import { FieldOutline } from '@/shared/ui/field-outline'
import { FloatingLabel } from '@/shared/ui/floating-label'
import {
  Select,
  SelectContent,
  SelectGroup,
  SelectTrigger,
  SelectValue,
} from '@/shared/ui/select'

type FloatingSelectProps = ComponentProps<typeof Select> & {
  label: string
  placeholder?: string
  hint?: ReactNode
  tone?: 'normal' | 'error' | 'disabled'
  className?: string
  children: ReactNode
}

const TONES = {
  normal: 'bg-surface-sunken text-foreground',
  error: 'bg-destructive/8 text-destructive-text',
  disabled: 'bg-veil/2 text-fg-disabled',
} as const

/**
 * The Verdant select: the same 44px field shell as `FloatingField`, except the
 * label never sits centred — a select always shows a value, so the label lives
 * at the top from the start.
 */
export function FloatingSelect({
  label,
  placeholder,
  hint,
  tone = 'normal',
  className,
  children,
  ...props
}: FloatingSelectProps) {
  const id = useId()
  const hintId = `${id}-hint`

  return (
    <div className={cn('flex flex-col gap-1.5', className)}>
      <Select {...props}>
        <div className="group/field relative">
          <SelectTrigger
          id={id}
          aria-describedby={hint === undefined ? undefined : hintId}
          className={cn(
            // The outline draws the border; the trigger keeps only its fill.
            'relative h-control-lg w-full items-center rounded-lg border-transparent pr-8.5 pl-3',
            TONES[tone],
          )}
        >
            <FloatingLabel isRaised tone={tone === 'error' ? 'error' : tone}>
              {label}
            </FloatingLabel>
            <SelectValue placeholder={placeholder} />
          </SelectTrigger>
          <FieldOutline
            label={label}
            isRaised
            tone={tone === 'normal' ? 'normal' : tone}
            className={tone === 'normal' ? 'border-border-strong' : undefined}
          />
        </div>
        <SelectContent>
          <SelectGroup>{children}</SelectGroup>
        </SelectContent>
      </Select>

      {hint !== undefined && (
        <div
          id={hintId}
          className={cn(
            'text-caption',
            tone === 'error' ? 'text-destructive-text' : 'text-muted-foreground',
          )}
        >
          {hint}
        </div>
      )}
    </div>
  )
}
